import { Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts"
import { CATEGORY_EMOJIS, CATEGORY_DISPLAY } from "../lib/constants"

interface CategoryChartProps {
  data: {
    category: string
    amount: number
  }[]
  currencySymbol?: string
  onCategoryClick?: (category: string) => void
}

const COLORS = [
  "hsl(var(--primary))",
  "#eab308",
  "#22c55e",
  "#ef4444",
  "#3b82f6",
  "#a855f7",
  "#ec4899",
  "#f97316",
  "#14b8a6",
  "#6366f1",
]

export function CategoryChart({ data, currencySymbol = '$', onCategoryClick }: CategoryChartProps) {
  if (data.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-[280px] text-muted-foreground gap-2">
        <p className="text-sm">No category data for this period</p>
        <p className="text-xs opacity-60">Add an expense to see your breakdown</p>
      </div>
    )
  }

  const total = data.reduce((sum, d) => sum + d.amount, 0)

  const formattedData = data.map((item, index) => ({
    ...item,
    name: `${CATEGORY_EMOJIS[item.category] || ''} ${CATEGORY_DISPLAY[item.category] ?? item.category}`,
    fill: COLORS[index % COLORS.length],
  }))

  return (
    <div>
      <div className="flex items-baseline justify-between mb-0.5">
        <span className="text-base font-bold">Spending by Category</span>
        <span className="text-base font-bold text-primary">{currencySymbol}{total.toFixed(0)}</span>
      </div>

      <ResponsiveContainer width="100%" height={220}>
        <PieChart>
          <Pie
            data={formattedData}
            dataKey="amount"
            nameKey="name"
            cx="50%"
            cy="50%"
            outerRadius={100}
            onClick={onCategoryClick ? (data) => onCategoryClick((data as unknown as { category: string }).category) : undefined}
            style={{ cursor: onCategoryClick ? 'pointer' : 'default' }}
          />
          <Tooltip formatter={(value) => typeof value === 'number' ? `${currencySymbol}${value.toFixed(2)}` : value} />
        </PieChart>
      </ResponsiveContainer>

      <div className="mt-2 space-y-1.5">
        {formattedData.map((item, index) => (
          <div
            key={index}
            className={`flex items-center justify-between ${onCategoryClick ? 'cursor-pointer hover:opacity-70' : ''}`}
            onClick={onCategoryClick ? () => onCategoryClick(item.category) : undefined}
          >
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: item.fill }} />
              <span className="text-sm text-muted-foreground truncate">{item.name}</span>
            </div>
            <div className="flex items-center gap-2 ml-4 flex-shrink-0">
              <span className="text-xs text-muted-foreground">{((item.amount / total) * 100).toFixed(0)}%</span>
              <span className="text-sm font-medium tabular-nums">{currencySymbol}{item.amount.toFixed(0)}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
